import { useContext } from 'react'
import { DateTime } from 'luxon'
import Context from '../Context'

/**
 * One clock for the whole app. Every screen that prints a time asks this for it, so a kick off cannot
 * read as 19:00 on the grid and 16:00 on the board — which is exactly what happened while some screens
 * used the machine's zone and others used UTC.
 *
 * UTC is the default, because that is the zone the feeds, the logs and the database all speak, and a
 * trading desk that spans time zones needs one clock rather than each screen guessing.
 */

export const ZONES = [
  { value: 'UTC', label: 'UTC' },
  { value: 'local', label: 'This computer' }
]

const zoneOf = (settings) => (settings && settings.timeZone === 'local' ? 'local' : 'utc')

export const clockFor = (zone) => {
  const at = (ms) => DateTime.fromMillis(Number(ms), { zone })

  return {
    zone,
    // what to write next to a time so nobody has to guess which zone it is in
    label: zone === 'utc' ? 'UTC' : DateTime.local().toFormat('ZZZZ'),

    /**
     * A kick off, in the fewest words that still say when: the two days anybody is trading get named,
     * everything further out gets its date.
     */
    kickOff: (ms) => {
      const when = at(ms)
      const today = DateTime.now().setZone(zone).startOf('day')
      if (when.hasSame(today, 'day')) {
        return `Today ${when.toFormat('t')}`
      }
      if (when.hasSame(today.plus({ days: 1 }), 'day')) {
        return `Tomorrow ${when.toFormat('t')}`
      }
      return when.toFormat('d LLL t')
    },

    // a date and a time as separate columns, the way the event screen and the history lay them out
    date: (ms) => at(ms).toFormat('dd LLL'),
    time: (ms) => at(ms).toFormat('HH:mm'),
    seconds: (ms) => at(ms).toFormat('HH:mm:ss'),

    // one string, for a list where a kick off is a detail rather than a column
    stamp: (ms) => at(ms).toFormat('dd LLL HH:mm')
  }
}

export const useClock = () => {
  const { settings } = useContext(Context)
  return clockFor(zoneOf(settings))
}

export default useClock
